import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import styled from 'styled-components'
import { Button } from '@jobber-project/jobber-ui'
import { useMutation } from '@apollo/client'
import { useRouter } from 'next/router'
import {
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  getAdditionalUserInfo,
  deleteUser,
} from 'firebase/auth'

import GoogleLogoGColor from '../../assets/images/google-logo-g-color.svg'
import { COLORS } from '../../utils/colorUtils'
import Text from '../../components/Text'
import logger from '../app/logger'
import { isAuthenticating } from '../cache/cache'
import Spacer from '../../components/Spacer'
import { auth } from '../firebase'

import AuthSocialSignInButton from './AuthSocialSignInButton'
import AuthStepHead from './AuthStepHead'
import { CREATE_USER_MUTATION } from './authMutations'

const AuthOptions = styled.div`
  margin-bottom: 20px;
  width: 100%;
  max-width: 330px;
`

const ErrorText = styled(Text)`
  margin-bottom: 10px;
  font-size: 1.6rem;
  line-height: 1.1875em;
  color: ${COLORS.carnation};
`

const StyledText = styled(Text)`
  margin-bottom: 20px;
  font-size: 1.6rem;
  line-height: 1.1875em;

  & a {
    font-weight: 500;
    color: ${COLORS.electricViolet};
  }
`

type AdditionalUserInfo = {
  first_name: string
  last_name: string
  given_name?: string
  family_name?: string
  email: string
}

function AuthChooseMethod() {
  const { t } = useTranslation('auth')

  const router = useRouter()

  const [createUser, { loading }] = useMutation(CREATE_USER_MUTATION)

  const [submitting, setSubmitting] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [googleLoading, setGoogleLoading] = useState<boolean>(false)

  function getFormattedAdditionalUserInfo({
    first_name: firstName,
    last_name: lastName,
    given_name: givenName,
    family_name: familyName,
    email,
  }: AdditionalUserInfo) {
    try {
      if (givenName && familyName) {
        return {
          email,
          firstName: givenName,
          lastName: familyName,
          gender: null,
        }
      }
      if (firstName && lastName) {
        return { email, firstName, lastName, gender: null }
      }
      return { email, gender: null }
    } catch (err) {
      logger.error(err)
      return {}
    }
  }

  async function handleProvider(
    provider: GoogleAuthProvider | FacebookAuthProvider,
  ) {
    try {
      isAuthenticating(true)
      setSubmitting(true)

      if (errorMessage) setErrorMessage('')

      const result = await signInWithPopup(auth, provider)
      const additionalUserInfo = getAdditionalUserInfo(result)

      if (additionalUserInfo.isNewUser) {
        try {
          await createUser({
            variables: getFormattedAdditionalUserInfo(
              additionalUserInfo.profile as AdditionalUserInfo,
            ),
          })
        } catch (err) {
          await deleteUser(result.user)
          throw err
        }
      }

      await router.push('/profile')
    } catch (err) {
      logger.error(err)
      setErrorMessage(err.message)
      setSubmitting(false)
    } finally {
      isAuthenticating(false)
    }
  }

  async function handleGoogleClick() {
    if (!loading && !submitting && !googleLoading) {
      setGoogleLoading(true)
      const provider = new GoogleAuthProvider()
      await handleProvider(provider)
      setGoogleLoading(false)
    }
  }

  function handleEmailClick() {
    router.push('/auth/login')
  }

  return (
    <>
      <AuthStepHead title={t('signInOptions')} />
      <Spacer maxHeight="40px" minHeight="30px" />
      <AuthOptions>
        {!!errorMessage && <ErrorText>{errorMessage}</ErrorText>}
        <AuthSocialSignInButton
          loading={googleLoading}
          title={t('continueGoogle')}
          icon={<GoogleLogoGColor width={24} height={24} />}
          onClick={handleGoogleClick}
        />
        <Link passHref href="/auth/login">
          <Button
            fluid
            as="a"
            variant="primary"
            size="large"
            onClick={handleEmailClick}
          >
            {t('continueEmail')}
          </Button>
        </Link>
      </AuthOptions>
      <StyledText align="center">
        {t('notMemberYet')}
        <Link shallow href="/auth/register">
          <a>{t('registerHere')}</a>
        </Link>
      </StyledText>
    </>
  )
}

export default AuthChooseMethod
